import React from 'react'
import Truncate from 'react-truncate-markup'

import { DropdownAttributeDisplay } from 'features/views/attributes/DropdownAttributeDisplay'
import { useRecordManagerFieldValue } from 'features/views/RecordManager/hooks/useRecordManagerFieldValue'

import { Box } from 'ui/components/Box'
import { Icon } from 'ui/components/Icon'
import { Popup, PopupContent, PopupInner, PopupTrigger } from 'ui/components/Popup'
import { Skeleton } from 'ui/components/Skeleton'
import { Tag } from 'ui/components/Tag'
import { Body } from 'ui/components/Text'
import { stopPropagation } from 'ui/helpers/utilities'

import { useDropdownAttributeBoxedState } from './hooks/useDropdownAttributeBoxedState'
import { BaseAttribute } from './BaseAttribute'
import { HighlightsWidgetAttributeComponent, HighlightsWidgetAttributeItem } from './types'

import { DropdownAttributeBoxedLabelStyles } from './DropdownAttribute.css'

const MAX_OPTIONS = 2

type DropdownAttributeProps = {}

export const DropdownAttribute: HighlightsWidgetAttributeComponent<DropdownAttributeProps> = ({
    item,
    isLoading,
    size,
    displayStyle,
    ...props
}) => {
    const value = useRecordManagerFieldValue<string | string[]>(item.field.api_name)
    const isEmpty = !isLoading && (!value || (Array.isArray(value) && value.length < 1))

    if (displayStyle === 'boxed') {
        return (
            <DropdownAttributeBoxed
                {...props}
                item={item}
                isLoading={isLoading}
                size={size}
                displayStyle={displayStyle}
                isEmpty={isEmpty}
            />
        )
    }

    return (
        <BaseAttribute
            {...props}
            displayStyle={displayStyle}
            item={item}
            isEmpty={isEmpty}
            isLoading={isLoading}
            size={size}
        >
            <DropdownAttributeDisplay
                value={value}
                field={item.field}
                isLoading={isLoading}
                maxLines={1}
                maxOptions={MAX_OPTIONS}
                size={size}
            />
        </BaseAttribute>
    )
}

type DropdownAttributeBoxedProps = {
    item: HighlightsWidgetAttributeItem
    value?: string | string[]
    isLoading?: boolean
    size?: 'm' | 'l'
    isEmpty?: boolean
}

export const DropdownAttributeBoxed: HighlightsWidgetAttributeComponent<
    DropdownAttributeBoxedProps
> = ({ value, item, isLoading, size, isEmpty, ...props }) => {
    const {
        options,
        onOptionClick,
        attributeRef,
        isOverflowPopupOpen,
        onOverflowLabelMouseEnter,
        onOverflowLabelMouseLeave,
        onOverflowPopupOpenChange,
        onOverflowLabelFocus,
        onOverflowLabelBlur,
        visibleOptionsLength,
        onTruncate,
    } = useDropdownAttributeBoxedState({
        value,
        item,
        isLoading,
    })

    return (
        <BaseAttribute
            ref={attributeRef}
            {...props}
            item={item}
            isEmpty={isEmpty}
            isLoading={isLoading}
            size={size}
        >
            <Truncate
                lines={1}
                onTruncate={onTruncate}
                ellipsis={
                    <OverflowOptions
                        options={options}
                        visibleOptionsLength={visibleOptionsLength}
                        isOverflowPopupOpen={isOverflowPopupOpen}
                        onOverflowLabelMouseEnter={onOverflowLabelMouseEnter}
                        onOverflowLabelMouseLeave={onOverflowLabelMouseLeave}
                        onOverflowPopupOpenChange={onOverflowPopupOpenChange}
                        onOverflowLabelFocus={onOverflowLabelFocus}
                        onOverflowLabelBlur={onOverflowLabelBlur}
                        onOptionClick={onOptionClick}
                    />
                }
            >
                <Box
                    style={{
                        fontWeight: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: 'inherit',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}
                    minWidth={0}
                    maxWidth="full"
                >
                    <Skeleton isLoading={isLoading}>
                        {options.map((option, idx) => (
                            <Truncate.Atom key={option.value}>
                                {idx > 0 && (
                                    <Icon
                                        name="Dot"
                                        size="xl"
                                        display="inline"
                                        verticalAlign="middle"
                                    />
                                )}
                                <Body
                                    size={size}
                                    style={{
                                        fontSize: 'inherit',
                                        lineHeight: 'inherit',
                                        fontWeight: 'inherit',
                                        whiteSpace: 'nowrap',
                                        maxWidth: options.length > 1 ? 'calc(100% - 50px)' : '100%',
                                    }}
                                    className={DropdownAttributeBoxedLabelStyles.styleFunction({
                                        color: option.color as any,
                                    })}
                                    trim
                                    shrink
                                >
                                    {option.label}
                                </Body>
                            </Truncate.Atom>
                        ))}
                    </Skeleton>
                </Box>
            </Truncate>
        </BaseAttribute>
    )
}

type OverflowOptionsProps = Pick<
    ReturnType<typeof useDropdownAttributeBoxedState>,
    | 'options'
    | 'isOverflowPopupOpen'
    | 'onOverflowLabelMouseEnter'
    | 'onOverflowLabelMouseLeave'
    | 'onOverflowPopupOpenChange'
    | 'onOverflowLabelFocus'
    | 'onOverflowLabelBlur'
> & {
    visibleOptionsLength: number
    onOptionClick: (e: React.MouseEvent<HTMLElement>) => void
}

const OverflowOptions: React.FC<OverflowOptionsProps> = ({
    options,
    visibleOptionsLength,
    isOverflowPopupOpen,
    onOverflowLabelMouseEnter,
    onOverflowLabelMouseLeave,
    onOverflowPopupOpenChange,
    onOverflowLabelFocus,
    onOverflowLabelBlur,
}) => {
    const overflowingOptionsLen = options.length - visibleOptionsLength
    const overflowingOptions = options.slice(visibleOptionsLength, options.length)

    if (overflowingOptionsLen > 0) {
        return (
            <Popup open={isOverflowPopupOpen} onOpenChange={onOverflowPopupOpenChange}>
                <PopupTrigger
                    onClick={stopPropagation}
                    onMouseEnter={onOverflowLabelMouseEnter}
                    onMouseLeave={onOverflowLabelMouseLeave}
                    onFocus={onOverflowLabelFocus}
                    onBlur={onOverflowLabelBlur}
                    style={{
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        lineHeight: 'inherit',
                    }}
                    asChild
                >
                    <Box display="inline">
                        <Icon name="Dot" size="xl" display="inline" verticalAlign="middle" />
                        <Body
                            style={{
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                lineHeight: 'inherit',
                            }}
                        >
                            +{overflowingOptionsLen}
                        </Body>
                    </Box>
                </PopupTrigger>
                <PopupContent
                    sideOffset={0}
                    side="bottom"
                    align="end"
                    onOpenAutoFocus={stopPropagation}
                    onCloseAutoFocus={stopPropagation}
                    onMouseEnter={onOverflowLabelMouseEnter}
                    onMouseLeave={onOverflowLabelMouseLeave}
                    style={{
                        width: 'auto',
                        maxWidth: '300px',
                    }}
                >
                    <PopupInner flex center gap="xs" wrap>
                        {overflowingOptions.map((option) => (
                            <Tag
                                color={option.color}
                                size="xs"
                                key={option.value}
                                noShrink
                                pointerEvents="none"
                                mb="xs"
                                tabIndex={-1}
                                minWidth={0}
                                maxWidth="full"
                            >
                                <Box trim maxWidth="full" py="3xs">
                                    {option.label}
                                </Box>
                            </Tag>
                        ))}
                    </PopupInner>
                </PopupContent>
            </Popup>
        )
    }

    return <>...</>
}
